import React, { useEffect, useState } from 'react';
import { IntakeProvider, useIntake } from '../context/IntakeContext';
import { ProgressBar } from './ui/ProgressBar';
import { Button } from './ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { validateStep } from '../utils/validateStep';
import { Step1Welcome } from './steps/Step1Welcome';
import { Step2Contact } from './steps/Step2Contact';
import { Step3Team } from './steps/Step3Team';
import { Step4Company } from './steps/Step4Company';
import { Step5Story } from './steps/Step5Story';
import { Step6Legal } from './steps/Step6Legal';
import { Step7Branding } from './steps/Step7Branding';
import { Step8Goals } from './steps/Step8Goals';
import { Step9Type } from './steps/Step9Type';
import { Step10Pages } from './steps/Step10Pages';
import { Step11Content } from './steps/Step11Content';
import { Step12Tech } from './steps/Step12Tech';
import { Step13SEO } from './steps/Step13SEO';
import { Step14Planning } from './steps/Step14Planning';
import { Step15Summary } from './steps/Step15Summary';
const stepTitles = ['Welkom', 'Contactgegevens', 'Team', 'Bedrijfsgegevens', 'Verhaal & Positionering', 'Juridisch', 'Branding', 'Doelen', 'Type Website', "Pagina's", 'Content', 'Techniek', 'SEO', 'Planning', 'Samenvatting'];
function IntakeFormContent() {
  const {
    currentStep,
    setCurrentStep,
    totalSteps,
    data
  } = useIntake();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  // Validate current step whenever data changes
  useEffect(() => {
    const validation = validateStep(currentStep, data);
    setValidationErrors(validation.errors);
    setShowErrors(false); // Reset error display when data changes
  }, [currentStep, data]);
  const handleNext = () => {
    const validation = validateStep(currentStep, data);
    if (!validation.isValid) {
      setShowErrors(true);
      // Scroll to top to show errors
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (currentStep < totalSteps) {
      setShowErrors(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setCurrentStep(currentStep + 1);
    }
  };
  const handlePrev = () => {
    if (currentStep > 1) {
      setShowErrors(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setCurrentStep(currentStep - 1);
    }
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Welcome key="step1" data-id="element-1" />;
      case 2:
        return <Step2Contact key="step2" data-id="element-2" />;
      case 3:
        return <Step3Team key="step3" data-id="element-3" />;
      case 4:
        return <Step4Company key="step4" data-id="element-4" />;
      case 5:
        return <Step5Story key="step5" data-id="element-5" />;
      case 6:
        return <Step6Legal key="step6" data-id="element-6" />;
      case 7:
        return <Step7Branding key="step7" data-id="element-7" />;
      case 8:
        return <Step8Goals key="step8" data-id="element-8" />;
      case 9:
        return <Step9Type key="step9" data-id="element-9" />;
      case 10:
        return <Step10Pages key="step10" data-id="element-10" />;
      case 11:
        return <Step11Content key="step11" data-id="element-11" />;
      case 12:
        return <Step12Tech key="step12" data-id="element-12" />;
      case 13:
        return <Step13SEO key="step13" data-id="element-13" />;
      case 14:
        return <Step14Planning key="step14" data-id="element-14" />;
      case 15:
        return <Step15Summary key="step15" data-id="element-15" />;
      default:
        return <Step1Welcome key="step1" data-id="element-16" />;
    }
  };
  const showBottomNav = currentStep > 1 && currentStep < totalSteps;
  const hasErrors = validationErrors.length > 0;
  return <div className="min-h-screen bg-[#F7F8FA] text-gray-900" data-id="element-17">
      {currentStep > 1 && <ProgressBar currentStep={currentStep} totalSteps={totalSteps} title={stepTitles[currentStep - 1]} data-id="element-18" />}

      <main className={`px-4 pt-8 md:pt-12 ${showBottomNav ? 'pb-40' : 'pb-16'}`} data-id="element-19">
        <AnimatePresence mode="wait" data-id="element-20">
          {showErrors && hasErrors && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="max-w-3xl mx-auto mb-6" data-id="element-21">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl" data-id="element-22">
                <div className="flex items-start" data-id="element-23">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-24" />
                  <div data-id="element-25">
                    <h3 className="text-sm font-semibold text-red-800 mb-1" data-id="element-26">
                      Vul alle verplichte velden in
                    </h3>
                    <ul className="text-sm text-red-700 space-y-1" data-id="element-27">
                      {validationErrors.map((error, index) => <li key={index} data-id="element-28">• {error}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        <AnimatePresence mode="wait" data-id="element-29">{renderStep()}</AnimatePresence>
      </main>

      {showBottomNav && <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 p-4 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-id="element-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between" data-id="element-31">
            <Button variant="outline" onClick={handlePrev} size="md" data-id="element-32">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-33" />
              Vorige
            </Button>
            <span className="text-xs text-gray-400 hidden sm:block" data-id="element-34">
              {stepTitles[currentStep - 1]}
            </span>
            <Button onClick={handleNext} size="md" className={hasErrors && showErrors ? 'opacity-75' : ''} data-id="element-35">
              Volgende
              <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-36" />
            </Button>
          </div>
        </div>}
    </div>;
}
export function IntakeForm() {
  return <IntakeProvider data-id="element-37">
      <IntakeFormContent data-id="element-38" />
    </IntakeProvider>;
}